import { useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Shield, Crown } from 'lucide-react';
import InfoTooltip from '../../components/ui/InfoTooltip';
import { fetchLeaderboardThunk } from '../../redux/gamification/gamificationActionThunks';
import { fetchCredLeaderboardThunk } from '../../redux/credibility/credibilityActionThunks';
import { selectCredLeaderboard } from '../../redux/credibility/credibilitySlice';
import BuildingTrackRecordBadge from '../../components/profile/BuildingTrackRecordBadge';
import CredibilityChip from '../../components/Credibility/CredibilityChip';

// FLIP-technique rank-change transition: a re-sort reorders these rows via
// plain flexbox document flow (no absolute positioning), which a CSS
// `transition` alone can't animate -- it only animates a property changing
// on one element, not a layout shift caused by siblings swapping places.
// This measures each row's position before and after a data update, applies
// an inverse transform so it visually hasn't moved yet, then transitions
// that back to zero -- so a re-sort reads as a slide, not a jarring snap.
// Deliberately not animating anything when rank is unchanged (delta === 0).
function useFlipRows(items) {
  const nodesRef = useRef(new Map());
  const rectsRef = useRef(new Map());

  useLayoutEffect(() => {
    const nextRects = new Map();
    nodesRef.current.forEach((node, key) => {
      if (node) nextRects.set(key, node.getBoundingClientRect());
    });

    // Respects both OS-level detection AND the explicit, persisted
    // Settings > Accessibility preference (see index.css's "ACCESSIBILITY
    // SETTINGS" section / App.js).
    const reduceMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ||
      document.documentElement.classList.contains('reduce-motion-forced');

    if (!reduceMotion) {
      nodesRef.current.forEach((node, key) => {
        if (!node) return;
        const prev = rectsRef.current.get(key);
        const next = nextRects.get(key);
        if (!prev || !next) return;
        const deltaY = prev.top - next.top;
        if (deltaY === 0) return;
        node.style.transition = 'none';
        node.style.transform = `translateY(${deltaY}px)`;
        requestAnimationFrame(() => {
          node.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
          node.style.transform = '';
        });
      });
    }

    rectsRef.current = nextRects;
  }, [items]);

  return (key) => (node) => {
    if (node) nodesRef.current.set(key, node);
    else nodesRef.current.delete(key);
  };
}

// Pass 28, Phase 5: a dedicated, shareable /leaderboard route. Both
// leaderboards already existed and worked -- they just lived only inside
// Profile -> Achievements, unreachable by direct link or bookmark
// (17_FRONTEND_RETENTION_MECHANICS.md, "No dedicated /leaderboard route").
// Same data/thunks/adapters as profile.jsx's leaderboard section, wrapped in
// its own page rather than refactored out of profile.jsx, to avoid touching
// a large, already-working file for a purely additive feature.

const first = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '') ?? undefined;

const adaptLeaderboardRow = (p) => {
  if (!p || typeof p !== 'object') return null;
  return {
    userId: first(p.user_id, p.id),
    username: String(first(p.username, p.full_name, `User ${first(p.user_id, p.id) || ''}`)),
    total: Number(first(p.total_points, p.points, 0)) || 0,
    badgesCount: Number(first(p.badges_count, p.badge_count, 0)) || 0,
  };
};

const adaptCredLeaderboardRow = (c) => {
  if (!c || typeof c !== 'object') return null;
  return {
    userId: c.user_id,
    username: String(first(c.username, `User ${c.user_id ?? ''}`)),
    score: Number(c.global_score) || 0,
    tier: c.status_tier || 'Beginner',
    accuracy: Number(first(c.combined_accuracy, c.base_accuracy, 0)) || 0,
    totalPredictions: Number(first(c.total_predictions, 0)) || 0,
  };
};

const rankBadgeClass = (index) =>
  index === 0
    ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
    : index === 1
    ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white'
    : index === 2
    ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
    : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300';

// Matches LeaderboardRow's real shape (rank badge, name block, right-aligned
// metric block) so the swap from skeleton to real rows doesn't reflow.
function LeaderboardRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl bg-gray-100 dark:bg-gray-700/30 animate-pulse">
      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
        <div className="space-y-2">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-28" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
        </div>
      </div>
      <div className="space-y-2 text-right">
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-12 ml-auto" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10 ml-auto" />
      </div>
    </div>
  );
}

function LeaderboardRow({ player, userId, index, metricLabel, metricValue, subline, showConfidenceBadge, credChip }) {
  const { t } = useTranslation();
  const isYou = player.userId === userId;
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl transition-all ${
        isYou
          ? 'bg-gradient-to-r from-indigo-600/20 to-violet-600/20 border-2 border-indigo-500/50'
          : 'bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${rankBadgeClass(index)}`}>
          {index === 0 ? '🏆' : index + 1}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2 flex-wrap">
            <span className="truncate">{player.username}</span>
            {isYou && <span className="text-indigo-400 flex-shrink-0">{t('credibility.leaderboardPage.you')}</span>}
            {showConfidenceBadge && (
              <BuildingTrackRecordBadge totalPredictions={player.totalPredictions} size="sm" />
            )}
            {credChip && <CredibilityChip cred={credChip} size="sm" />}
          </div>
          {subline && <div className="text-sm text-gray-600 dark:text-gray-400">{subline}</div>}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-bold text-base sm:text-lg text-indigo-600 dark:text-indigo-400 whitespace-nowrap">{metricValue}</div>
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{metricLabel}</div>
      </div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.profile?.user?.id);

  const gamificationLeaderboardRaw = useSelector((s) => s.gamification?.leaderboard);
  const rawCredLeaderboard = useSelector(selectCredLeaderboard);

  // gamification.leaderboard starts out `null` (not `[]`) until fetched, so
  // that's the reliable "already loaded" signal -- an empty array after a
  // real fetch would otherwise look identical to "never fetched".
  useEffect(() => {
    if (rawCredLeaderboard.length === 0) dispatch(fetchCredLeaderboardThunk({ limit: 25 }));
    if (!gamificationLeaderboardRaw) dispatch(fetchLeaderboardThunk(25));
  }, [dispatch, rawCredLeaderboard.length, gamificationLeaderboardRaw]);

  const credLeaderboard = useMemo(
    () => (Array.isArray(rawCredLeaderboard) ? rawCredLeaderboard : []).map(adaptCredLeaderboardRow).filter(Boolean),
    [rawCredLeaderboard]
  );
  const activityLeaderboard = useMemo(() => {
    const rawLeaderboard = gamificationLeaderboardRaw || [];
    const rows = Array.isArray(rawLeaderboard) ? rawLeaderboard : Array.isArray(rawLeaderboard?.users) ? rawLeaderboard.users : [];
    return rows.map(adaptLeaderboardRow).filter(Boolean);
  }, [gamificationLeaderboardRaw]);

  const setCredRowRef = useFlipRows(credLeaderboard);
  const setActivityRowRef = useFlipRows(activityLeaderboard);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10 sm:space-y-12">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('credibility.leaderboardPage.pageTitle')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('credibility.leaderboardPage.pageSubtitle')}
        </p>
      </div>

      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
          <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-500 flex-shrink-0" />
          {t('credibility.leaderboardPage.credTitle')}
          <InfoTooltip text={t('credibility.leaderboardPage.scoreTooltip')} />
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {t('credibility.leaderboardPage.credSubtitle')}
        </p>
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 overflow-hidden">
          <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {gamificationLeaderboardRaw === null ? (
              Array.from({ length: 5 }).map((_, i) => <LeaderboardRowSkeleton key={i} />)
            ) : credLeaderboard.length > 0 ? (
              credLeaderboard.map((player, index) => (
                <div key={player.userId || `cred-${index}`} ref={setCredRowRef(player.userId ?? `cred-${index}`)}>
                  <LeaderboardRow
                    player={player}
                    userId={userId}
                    index={index}
                    metricValue={player.score.toFixed(1)}
                    metricLabel={t('credibility.leaderboardPage.scoreLabel')}
                    credChip={{ score: player.score, tier: player.tier, accuracyPct: player.accuracy * 100 }}
                    showConfidenceBadge
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm py-6 text-center">{t('credibility.leaderboardPage.credEmpty')}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-3">
          <Crown className="w-6 h-6 sm:w-7 sm:h-7 text-violet-400 flex-shrink-0" />
          {t('credibility.leaderboardPage.activityTitle')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {t('credibility.leaderboardPage.activitySubtitle')}
        </p>
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 overflow-hidden">
          <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {gamificationLeaderboardRaw === null ? (
              Array.from({ length: 5 }).map((_, i) => <LeaderboardRowSkeleton key={i} />)
            ) : activityLeaderboard.length > 0 ? (
              activityLeaderboard.map((player, index) => (
                <div key={player.userId || `activity-${index}`} ref={setActivityRowRef(player.userId ?? `activity-${index}`)}>
                  <LeaderboardRow
                    player={player}
                    userId={userId}
                    index={index}
                    metricValue={t('credibility.leaderboardPage.xpValue', { count: player.total })}
                    metricLabel={t('credibility.leaderboardPage.badgesLabel', { count: player.badgesCount })}
                    subline={t('credibility.leaderboardPage.levelSubline', { level: Math.floor(player.total / 100) + 1 })}
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm py-6 text-center">{t('credibility.leaderboardPage.activityEmpty')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
